import React, { useState, useMemo } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator, TextInput, TouchableOpacity } from 'react-native';
import { Text, Card, DataTable, useTheme, Button, Dialog, Portal, IconButton } from 'react-native-paper';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, addDays } from 'date-fns';
import { hr } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { DateFilters } from '../../../employees/components/admin-controls/DateFilters';
import { attendanceAPI, AttendanceResponse, EmployeeAttendance } from './api';
import { useAuth } from '../../../../../hooks/useAuth';

interface Props {
  locationId: string;
}

export const WebLocationAttendance: React.FC<Props> = ({ locationId }) => {
  const theme = useTheme();
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedFilter, setSelectedFilter] = useState<string | null>('Ovaj mjesec');
  const [dateRange, setDateRange] = useState({
    start: startOfMonth(selectedDate),
    end: endOfMonth(selectedDate)
  });
  
  // Stanje za odabir mjeseca i godine
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const quickFilters = [
    { label: 'Ovaj mjesec', date: startOfMonth(new Date()) },
    { label: 'Prošli mjesec', date: startOfMonth(addDays(startOfMonth(new Date()), -1)) },
    { label: 'Odaberi mjesec', date: new Date() },
  ];

  const handleQuickFilter = (filter: string, date: Date) => {
    setSelectedFilter(filter);
    setSelectedDate(date);

    let start, end;
    
    if (filter === 'Odaberi mjesec') {
      setShowMonthYearPicker(true);
      return;
    } else {
      // Za sve ostale filtere (mjesece)
      start = startOfMonth(date);
      end = endOfMonth(date);
    }

    setDateRange({ start, end });
  };
  
  // Funkcija za odabir mjeseca i godine
  const handleSelectMonthYear = () => {
    const newDate = new Date(selectedYear, selectedMonth, 1);
    setSelectedDate(newDate);
    setSelectedFilter('Odabrani mjesec');
    setDateRange({
      start: startOfMonth(newDate),
      end: endOfMonth(newDate)
    });
    setShowMonthYearPicker(false);
  };
  
  // Generiranje popisa mjeseci
  const months = [
    'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj',
    'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'
  ];
  
  // Generiranje popisa godina (5 godina unatrag i 5 godina unaprijed)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  const { data: rawData, isLoading, error } = useQuery<AttendanceResponse>({
    queryKey: ['attendance', locationId, format(dateRange.start, 'yyyy-MM-dd'), format(dateRange.end, 'yyyy-MM-dd')],
    queryFn: () => attendanceAPI.getLocationAttendance(
      locationId,
      format(dateRange.start, 'yyyy-MM-dd'),
      format(dateRange.end, 'yyyy-MM-dd'),
      token || ''
    ),
    enabled: !!token && !!locationId
  });
  
  // Koristimo podatke direktno iz API-ja jer smo već riješili problem duplikata u api.ts
  const data = rawData;

  const handleExportPDF = () => {
    alert('Imenjače, ovo ćemo danas srediti do kraja dana.');
    // TODO: Implementirati PDF export
  };

  const getDayLabel = (date: Date) => {
    return format(date, 'd');
  };

  const getDates = () => {
    return eachDayOfInterval({
      start: dateRange.start,
      end: dateRange.end
    });
  };

  const renderEmployeeRow = (employee: EmployeeAttendance, index: number) => {
    const dates = getDates();
    const isLastRow = index === data?.employees?.length - 1;
    
    // Koristimo poseban stil za zadnji red kako bismo uklonili podvlačenje
    const rowStyle = {
      ...index % 2 === 0 ? styles.evenRow : styles.oddRow,
      ...(isLastRow && { borderBottomWidth: 0 }) // Uklanjamo podvlačenje za zadnji red
    };
    
    return (
      <DataTable.Row key={employee.employeeId} style={rowStyle}>
        <DataTable.Cell style={styles.nameColumn}>
          <Text style={styles.employeeName}>
            {employee.employeeName}
          </Text>
        </DataTable.Cell>
        
        {dates.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const record = employee.records[dateStr];
          const isPresent = record?.present || false;
          
          return (
            <DataTable.Cell 
              key={`${employee.employeeId}-${dateStr}`}
              style={styles.dateCell}
            >
              <View style={[styles.attendanceBox, isPresent ? styles.presentBox : styles.absentBox]}>
                <Text style={[styles.cellText, isPresent ? styles.presentText : styles.absentText]}>
                  {format(date, 'd')}
                </Text>
              </View>
            </DataTable.Cell>
          );
        })}
      </DataTable.Row>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 16 }}>Učitavanje evidencije prisutnosti...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>Došlo je do greške prilikom učitavanja podataka.</Text>
        <Button mode="contained" onPress={() => refetch()} style={{ marginTop: 16 }}>
          Pokušaj ponovno
        </Button>
      </View>
    );
  }

  if (!data || !data.employees || data.employees.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Nema podataka o prisutnosti za odabrano razdoblje.</Text>
      </View>
    );
  }

  const dates = getDates();

  return (
    <View style={styles.container}>
      <View style={styles.pageHeader}>
        <Text style={styles.pageTitle}>Evidencija prisutnosti</Text>
        <Text style={styles.pageSubtitle}>Pregled prisutnosti djelatnika po danima</Text>
      </View>
      
      <Card style={styles.filterCard}>
        <Card.Content>
          <View style={styles.filterContainer}>
            <View style={styles.filterRow}>
              <DateFilters
                displayDate={selectedDate}
                onDateChange={setSelectedDate}
                tempDate={selectedDate}
                onTempDateChange={setSelectedDate}
                showDatePicker={false}
                onToggleDatePicker={() => {}}
                quickFilters={quickFilters}
                selectedFilter={selectedFilter}
                onQuickFilterSelect={handleQuickFilter}
                hideCalendar={true}
              />
              {/* Gumb za odabir mjeseca uklonjen jer je već uključen u filterima */}
              <Button
                mode="contained"
                onPress={handleExportPDF}
                style={styles.exportButton}
              >
                PDF
              </Button>
            </View>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.tableCard}>
        <Card.Content>
          <View style={styles.tableContainer}>
            <View style={styles.monthHeader}>
              <Text style={styles.monthTitle}>
                {format(dateRange.start, 'MMMM yyyy', { locale: hr })}
              </Text>
              <Text style={styles.recordsCount}>
                {data.employees.length} {data.employees.length === 1 ? 'djelatnik' : 'djelatnika'}
              </Text>
            </View>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={true}
              style={styles.scrollContainer}
              contentContainerStyle={styles.scrollContentContainer}
            >
              <DataTable>
                <DataTable.Header style={styles.tableHeader}>
                  <DataTable.Title style={styles.nameColumn}>
                    <Text style={styles.headerText}>Djelatnik</Text>
                  </DataTable.Title>
                  
                  {dates.map(date => (
                    <DataTable.Title 
                      key={format(date, 'yyyy-MM-dd')} 
                      style={styles.dateCell}
                    >
                      <View style={styles.dayHeaderBox}>
                        <Text style={styles.dayLetter}>
                          {format(date, 'E', { locale: hr }).charAt(0)}
                        </Text>
                      </View>
                    </DataTable.Title>
                  ))}
                </DataTable.Header>

                {data?.employees?.map((employee, index) => (
                  renderEmployeeRow(employee, index)
                ))}
              </DataTable>
            </ScrollView>
          </View>
        </Card.Content>
      </Card>
      
      <Portal>
        <Dialog 
          visible={showMonthYearPicker} 
          onDismiss={() => setShowMonthYearPicker(false)}
          style={styles.dialog}
        >
          <Dialog.Title style={styles.dialogTitle}>
            Odaberi mjesec i godinu
          </Dialog.Title>
          <Dialog.Content>
            <View style={styles.monthYearPickerContainer}>
              <View style={styles.monthYearRow}>
                <View style={styles.monthSelector}>
                  <Text style={styles.pickerLabel}>Mjesec:</Text>
                  <View style={styles.pickerWrapper}>
                    <TouchableOpacity 
                      onPress={() => setSelectedMonth(prev => prev > 0 ? prev - 1 : 11)}
                      style={styles.arrowButton}
                    >
                      <Text style={styles.arrowText}>◀</Text>
                    </TouchableOpacity>
                    <Text style={styles.monthYearValue}>{months[selectedMonth]}</Text>
                    <TouchableOpacity 
                      onPress={() => setSelectedMonth(prev => prev < 11 ? prev + 1 : 0)}
                      style={styles.arrowButton}
                    >
                      <Text style={styles.arrowText}>▶</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
              
              <View style={styles.monthYearRow}>
                <View style={styles.yearSelector}>
                  <Text style={styles.pickerLabel}>Godina:</Text>
                  <View style={styles.pickerWrapper}>
                    <TouchableOpacity 
                      onPress={() => setSelectedYear(prev => prev - 1)}
                      style={styles.arrowButton}
                    >
                      <Text style={styles.arrowText}>◀</Text>
                    </TouchableOpacity>
                    <Text style={styles.monthYearValue}>{selectedYear}</Text>
                    <TouchableOpacity 
                      onPress={() => setSelectedYear(prev => prev + 1)}
                      style={styles.arrowButton}
                    >
                      <Text style={styles.arrowText}>▶</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
              
              <Text style={styles.selectedPeriodText}>
                Odabrano: <Text style={styles.selectedPeriodValue}>{months[selectedMonth]} {selectedYear}</Text>
              </Text>
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowMonthYearPicker(false)} textColor="#64748B">Odustani</Button>
            <Button onPress={handleSelectMonthYear} mode="contained" style={styles.confirmButton}>Potvrdi</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      
      <View style={styles.legendContainer}>
        <View style={styles.legendItem}>
          <View style={styles.legendBox}>
            <View style={[styles.attendanceBox, styles.presentBox, { width: 16, height: 16 }]} />
          </View>
          <Text style={styles.legendText}>Prisutan</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={styles.legendBox}>
            <View style={[styles.attendanceBox, styles.absentBox, { width: 16, height: 16 }]} />
          </View>
          <Text style={styles.legendText}>Odsutan</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#F8FAFC',
  },
  pageHeader: {
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  filterCard: {
    marginBottom: 16,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderLeftWidth: 4,
    borderLeftColor: '#3B82F6',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  },
  filterContainer: {
    marginBottom: 0,
  },
  filterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  exportButton: {
    backgroundColor: '#1E40AF',
    marginLeft: 8,
  },
  monthYearButton: {
    backgroundColor: '#4F46E5',
    marginLeft: 8,
  },
  dialog: {
    maxWidth: 400,
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
  },
  dialogTitle: {
    fontSize: 18,
    textAlign: 'center',
    color: '#1E293B',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Ikona kalendara uklonjena
  monthYearPickerContainer: {
    marginTop: 8,
    marginBottom: 16,
    alignItems: 'center',
    width: '100%',
  },
  monthYearRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 16,
    width: '100%',
  },
  monthSelector: {
    width: '100%',
  },
  yearSelector: {
    width: '100%',
  },
  pickerLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  pickerWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    padding: 0,
    outlineStyle: 'none',
  },
  monthYearValue: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    flex: 1,
    textAlign: 'center',
  },
  arrowButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 18,
    color: '#3B82F6',
    fontWeight: '600',
  },
  selectedPeriodText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
  },
  selectedPeriodValue: {
    fontWeight: '600',
    color: '#1E293B',
  },
  confirmButton: {
    backgroundColor: '#1E40AF',
    borderRadius: 8,
  },
  tableCard: {
    marginBottom: 16,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderLeftWidth: 4,
    borderLeftColor: '#10B981',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  scrollContainer: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  scrollContentContainer: {
    paddingBottom: 8,
  },
  monthHeader: {
    backgroundColor: '#E2E8F0',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  monthTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    textTransform: 'capitalize',
  },
  recordsCount: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
  },
  legendContainer: {
    flexDirection: 'row',
    marginTop: 8,
    marginBottom: 16,
    justifyContent: 'center',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
  },
  legendBox: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
    outlineStyle: 'none',
  },
  legendText: {
    fontSize: 12,
    color: '#64748B',
  },
  tableHeader: {
    backgroundColor: '#F1F5F9',
  },
  headerText: {
    fontWeight: '600',
    fontSize: 12,
    textAlign: 'center',
    color: '#212529',
  },
  nameColumn: {
    flex: 2,
    justifyContent: 'flex-start',
    paddingLeft: 16,
    width: 180,
    minWidth: 180,
  },
  employeeName: {
    fontSize: 12,
    fontWeight: '500',
  },
  dateCell: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 2,
    width: 32,
    minWidth: 32,
    height: 40,
  },
  dayHeaderBox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayLetter: {
    fontSize: 10,
    fontWeight: '700',
    textAlign: 'center',
    color: '#475569',
  },
  attendanceBox: {
    width: 28,
    height: 28,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 1,
    outlineStyle: 'none',
  },
  presentBox: {
    backgroundColor: '#4ADE80', // Jača zelena boja
    borderWidth: 1,
    borderColor: '#22C55E',
  },
  absentBox: {
    backgroundColor: '#F87171', // Jača crvena boja
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  cellText: {
    fontSize: 12,
    textAlign: 'center',
  },
  presentText: {
    color: '#43A047', // Zelena
    fontWeight: '600',
  },
  absentText: {
    color: '#E53935', // Crvena
    fontWeight: '600',
  },
  evenRow: {
    backgroundColor: '#FFFFFF',
  },
  oddRow: {
    backgroundColor: '#FAFAFA', // Svijetlo siva za neparne retke
  },
  errorText: {
    color: '#E53935',
    fontWeight: '500',
  },
});
